/**
 * Pure expressive-graph gate for phone perf — rejects Steady / Clean / missing lanes.
 */
import {
  effectiveSound,
  isAudibleSound,
  type CameraMotionPreset,
  type LookTreatment,
  type ResolvedEdit,
  type SourcePack,
  type StickerOption,
  type TransitionPreset,
} from "@desk/core";

export interface ExpressiveGraphAssertion {
  ok: boolean;
  failures: string[];
  selected: {
    headlineText: string | null;
    musicAssetId: string | null;
    sfxAAssetId: string | null;
    sfxBAssetId: string | null;
    lookId: string | null;
    lookIntensityMilli: number | null;
    motionBeatId: string | null;
    motionPresetId: string | null;
    motionAmplitudeMilli: number | null;
    motionZoomDeltaMilli: number | null;
    stickerSlot: string | null;
    stickerId: string | null;
    transitionBeatId: string | null;
    transitionPresetId: string | null;
    transitionKind: string | null;
  };
  activeRanges: {
    music: boolean;
    sfxAAtTick: number | null;
    sfxBAtTick: number | null;
    stickerStartTick: number | null;
    stickerEndTick: number | null;
    transitionBoundaryEditHint: string | null;
    motionBeatStartTick: number | null;
    motionBeatEndTick: number | null;
    transitionBoundaryEditTick: number | null;
  };
}

const STEADY_RE = /steady|^motion-steady$/i;
const CLEAN_LOOK_RE = /clean|^look-clean$/i;
const HARD_CUT_RE = /hard-cut|hardcut|^transition-hard-cut$/i;

function catalogMotion(pack: SourcePack, id: string): CameraMotionPreset | undefined {
  return pack.cameraMotions?.find((m) => m.id === id);
}

function catalogLook(pack: SourcePack, id: string): LookTreatment | undefined {
  return pack.lookTreatments?.find((l) => l.id === id);
}

function catalogSticker(pack: SourcePack, id: string): StickerOption | undefined {
  return pack.stickerOptions?.find((s) => s.id === id);
}

function catalogTransition(pack: SourcePack, id: string): TransitionPreset | undefined {
  return pack.transitionPresets?.find((t) => t.id === id);
}

/**
 * Fail closed unless the resolved graph carries a real expressive creative setup
 * suitable for phone-perf measurement (nonzero motion, audible music, both SFX, etc.).
 */
export function assertExpressivePerfGraph(
  resolved: ResolvedEdit,
  pack: SourcePack,
): ExpressiveGraphAssertion {
  const failures: string[] = [];
  const selected: ExpressiveGraphAssertion["selected"] = {
    headlineText: null,
    musicAssetId: null,
    sfxAAssetId: null,
    sfxBAssetId: null,
    lookId: null,
    lookIntensityMilli: null,
    motionBeatId: null,
    motionPresetId: null,
    motionAmplitudeMilli: null,
    motionZoomDeltaMilli: null,
    stickerSlot: null,
    stickerId: null,
    transitionBeatId: null,
    transitionPresetId: null,
    transitionKind: null,
  };
  const activeRanges: ExpressiveGraphAssertion["activeRanges"] = {
    music: false,
    sfxAAtTick: null,
    sfxBAtTick: null,
    stickerStartTick: null,
    stickerEndTick: null,
    transitionBoundaryEditHint: null,
    motionBeatStartTick: null,
    motionBeatEndTick: null,
    transitionBoundaryEditTick: null,
  };

  const caption = resolved.captions[resolved.captions.length - 1];
  if (!caption || !caption.tokenIds?.length) {
    failures.push("missing valid text headline caption");
  } else {
    selected.headlineText = caption.tokenIds.join(" ");
  }

  const sound = effectiveSound(resolved);
  if (!sound || !isAudibleSound(sound) || !sound.assetId) {
    failures.push("missing audible music bed");
  } else {
    selected.musicAssetId = sound.assetId;
    activeRanges.music = true;
  }

  // Unlimited id-keyed sfx list now; an expressive graph places at least two
  // one-shots at distinct, meaningful moments.
  const sfxA = resolved.sfx[0];
  const sfxB = resolved.sfx[1];
  if (!sfxA) failures.push("first sfx missing or cleared");
  else {
    selected.sfxAAssetId = sfxA.assetId;
    activeRanges.sfxAAtTick = sfxA.atTick;
  }
  if (!sfxB) failures.push("second sfx missing or cleared");
  else {
    selected.sfxBAssetId = sfxB.assetId;
    activeRanges.sfxBAtTick = sfxB.atTick;
  }
  if (sfxA && sfxB) {
    if (sfxA.atTick === sfxB.atTick) {
      failures.push(`the first two sfx share atTick=${sfxA.atTick} (need distinct moments)`);
    }
    if (sfxA.atTick <= 0 && sfxB.atTick <= 0) {
      failures.push("sfx moments both at tick 0, need meaningful mid-timeline placements");
    }
  }

  if (!resolved.look?.treatmentId) {
    failures.push("look missing or cleared");
  } else if (
    CLEAN_LOOK_RE.test(resolved.look.treatmentId) ||
    catalogLook(pack, resolved.look.treatmentId)?.tags.includes("clean")
  ) {
    failures.push(`look is clean/no-op: ${resolved.look.treatmentId}`);
  } else {
    const look = catalogLook(pack, resolved.look.treatmentId);
    if (!look) failures.push(`look id not in pack catalog: ${resolved.look.treatmentId}`);
    else {
      const expressive =
        look.contrastMilli !== 1000 ||
        look.saturationMilli !== 1000 ||
        look.warmthMilli !== 1000 ||
        look.vignetteMilli > 0;
      if (!expressive) failures.push(`look catalog is identity/no-op: ${look.id}`);
    }
    selected.lookId = resolved.look.treatmentId;
    selected.lookIntensityMilli = resolved.look.intensityMilli ?? null;
    if ((resolved.look.intensityMilli ?? 0) <= 0) {
      failures.push("look intensityMilli is zero");
    }
  }

  const retained = new Set(resolved.beatOrder);
  let motionOk = false;
  for (const [beatId, op] of resolved.motions) {
    if (!retained.has(beatId) || !op.presetId) continue;
    if (STEADY_RE.test(op.presetId)) continue;
    const preset = catalogMotion(pack, op.presetId);
    if (!preset) {
      failures.push(`motion preset not in catalog: ${op.presetId}`);
      continue;
    }
    const amp = preset.amplitudeMilli ?? 0;
    const zoom = preset.zoomDeltaMilli ?? 0;
    if (amp <= 0 && zoom <= 0) {
      failures.push(`motion preset has zero catalog params: ${op.presetId}`);
      continue;
    }
    selected.motionBeatId = beatId;
    selected.motionPresetId = op.presetId;
    selected.motionAmplitudeMilli = amp;
    selected.motionZoomDeltaMilli = zoom;
    const beat = pack.beats.find((b) => b.id === beatId);
    activeRanges.motionBeatStartTick = beat?.startTick ?? null;
    activeRanges.motionBeatEndTick = beat?.endTick ?? null;
    motionOk = true;
    break;
  }
  if (!motionOk) failures.push("missing non-steady motion on a retained beat");

  let stickerOk = false;
  for (const op of resolved.stickers) {
    if (!op.stickerId) continue;
    const st = catalogSticker(pack, op.stickerId);
    if (!st) {
      failures.push(`sticker not in catalog: ${op.stickerId}`);
      continue;
    }
    selected.stickerSlot = op.id;
    selected.stickerId = op.stickerId;
    activeRanges.stickerStartTick = op.startTick;
    activeRanges.stickerEndTick = op.endTick;
    if (op.endTick <= op.startTick) {
      failures.push(`sticker range empty: ${op.stickerId}`);
      continue;
    }
    stickerOk = true;
    break;
  }
  if (!stickerOk) failures.push("missing non-null sticker active over a tick range");

  let transitionOk = false;
  for (const [beatId, op] of resolved.transitions) {
    if (!retained.has(beatId) || !op.presetId) continue;
    if (HARD_CUT_RE.test(op.presetId)) continue;
    const tr = catalogTransition(pack, op.presetId);
    if (!tr) {
      failures.push(`transition not in catalog: ${op.presetId}`);
      continue;
    }
    const isFlash = tr.id.includes("flash") || tr.tags.includes("flash");
    const isSwipe = tr.id.includes("swipe") || tr.tags.includes("swipe");
    if (!isFlash && !isSwipe) {
      failures.push(`transition is not flash/swipe: ${op.presetId}`);
      continue;
    }
    // Must not be last retained beat (resolver should omit, but fail closed).
    if (resolved.beatOrder[resolved.beatOrder.length - 1] === beatId) {
      failures.push(`transition on final retained beat: ${beatId}`);
      continue;
    }
    selected.transitionBeatId = beatId;
    selected.transitionPresetId = op.presetId;
    selected.transitionKind = isFlash ? "flash" : "swipe";
    activeRanges.transitionBoundaryEditHint = `outgoing:${beatId}`;
    const beat = pack.beats.find((b) => b.id === beatId);
    // Outgoing beat end is the retained boundary the flash/swipe straddles.
    activeRanges.transitionBoundaryEditTick = beat?.endTick ?? null;
    transitionOk = true;
    break;
  }
  if (!transitionOk) failures.push("missing flash/swipe transition on a retained boundary");

  return {
    ok: failures.length === 0,
    failures,
    selected,
    activeRanges,
  };
}

/** Reject page-rAF-only samples as a gate source. */
export function assertCommittedFrameSamples(
  intervalsMs: number[],
  opts: { minCount: number; method: string },
): { ok: boolean; failures: string[] } {
  const failures: string[] = [];
  if (opts.method !== "r3f-post-effect-frame-commit") {
    failures.push(`refusing non-commit method: ${opts.method}`);
  }
  if (intervalsMs.length < opts.minCount) {
    failures.push(`insufficient committed intervals: ${intervalsMs.length} < ${opts.minCount}`);
  }
  return { ok: failures.length === 0, failures };
}
